import { useEffect, useRef, useState } from "react";
import { fetchWithError } from "../utils/fetchWithError.ts";
import {
    Avatar,
    Box,
    Tab,
    Tabs,
    Typography,
    Container,
    Stack,
    useMediaQuery,
    useTheme,
    IconButton,
    CircularProgress,
    Tooltip,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Badge
} from "@mui/material";
import { PopulatedAccount } from "../../../src/core/types/user/account.interface";
import { IProfile } from "@ids/shared";
import { MessagesTab } from "./partials/MessagesTab.tsx";
import { AccountTab } from "./partials/AccountTab.tsx";
import { DashboardTab } from "./partials/DashboardTab.tsx";
import { SettingsTab } from "./partials/SettingsTab.tsx";
import { useSearchParams } from "react-router-dom";
import { InAppContainer } from "../container/InAppContainer.tsx";
import { PhotoCamera } from "@mui/icons-material";
import { TintoaPageBackground } from "@tintoa/ui";
import { useSnackbar } from "notistack";

const PROFILE_TABS = new Set([1, 3, 4, 5]);

const PROFILE_GOOGLE_LINK_ERRORS: Record<string, string> = {
    "1018": "Die Google-E-Mail stimmt nicht mit deinem Konto überein. Verknüpfe das Google-Konto, das dieselbe E-Mail-Adresse verwendet.",
    "1019": "Bitte melde dich an, um dein Konto mit Google zu verknüpfen.",
    "1005": "Dieser Zugang ist gesperrt. Bitte wende dich an unseren Support."
};

function resolveProfileTab(tabParam: string | null): number {
    if (!tabParam) {
        return 1;
    }
    const parsed = parseInt(tabParam, 10);
    return PROFILE_TABS.has(parsed) ? parsed : 1;
}

export const ProfileScreen = () => {
    const [profile, setProfile] = useState<IProfile>();
    const [account, setAccount] = useState<PopulatedAccount>();
    const [isUploading, setIsUploading] = useState(false);
    const [googleDialogOpen, setGoogleDialogOpen] = useState(false);
    const [messageCount, setMessageCount] = useState(0);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        // Only load profile data needed for header (avatar and name)
        loadProfile();
        // Account data is only needed for avatar upload check, load it lazily
        loadAccount();
    }, []);

    const loadProfile = async () => {
        fetchWithError("/profile", {
            credentials: "include"
        }).then((res: IProfile & { profile?: IProfile }) => {
            setProfile(res.profile ?? res);
        });
    };

    const loadAccount = async () => {
        fetchWithError("/session/account", {
            credentials: "include"
        }).then((acc) => {
            setAccount(acc);
        }).catch(() => {
            // Account loading failed, ignore
        });
    };

    const handleAvatarClick = () => {
        // Check if account is Google-linked
        if (account?.isGoogleLinked) {
            setGoogleDialogOpen(true);
            return;
        }
        // Only open file input if Google is not linked
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Check if account is Google-linked
        if (account?.isGoogleLinked) {
            setGoogleDialogOpen(true);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }

        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
            alert('Bitte nur JPEG, PNG oder WebP Dateien hochladen.');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            alert('Die Datei darf maximal 5MB groß sein.');
            return;
        }
        setIsUploading(true);
        const formData = new FormData();
        formData.append('image', file);
        try {
            const response = await fetchWithError<{ success: boolean; imageInfo: { data: string; contentType: string; uploadedAt: Date } }>("/profile/image", {
                method: "POST",
                body: formData,
                credentials: "include"
            });
            if (response.success) {
                setProfile(prev => {
                    if (!prev) return prev;
                    return { ...prev, profileImage: response.imageInfo };
                });
            }
        } catch (e) {
            // Display the specific error message from the backend
            if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: 'error' });
            } else {
                enqueueSnackbar("Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie den Vorgang erneut.", { variant: 'error' });
            }
        } finally {
            setIsUploading(false);
        }
    };

    const [params, setSearchParams] = useSearchParams();
    const [tab, setTab] = useState(() => resolveProfileTab(params.get("tab")));
    const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);

    // Watch for URL parameter changes and update tab state
    useEffect(() => {
        setTab(resolveProfileTab(params.get("tab")));
    }, [params]);

    useEffect(() => {
        const errorCode = params.get("error");
        const googleLinked = params.get("googleLinked");
        if (!errorCode && googleLinked !== "1") {
            return;
        }
        if (googleLinked === "1") {
            enqueueSnackbar("Dein Konto wurde mit Google verknüpft.", {
                variant: "success"
            });
        }
        if (errorCode) {
            const message =
                PROFILE_GOOGLE_LINK_ERRORS[errorCode] ??
                "Die Google-Verknüpfung ist fehlgeschlagen. Bitte versuche es erneut.";
            enqueueSnackbar(message, { variant: "error" });
        }
        const next = new URLSearchParams(params);
        next.delete("error");
        next.delete("googleLinked");
        setSearchParams(next, { replace: true });
    }, [params, enqueueSnackbar, setSearchParams]);

    const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTab(newValue);
    };

    return (
        <InAppContainer>
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'background.default',
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                {/* Background with blur effect for all screen sizes */}
                <TintoaPageBackground position="absolute" />

                {/* Content */}
                <Box sx={{ position: 'relative', zIndex: 1, flex: 1 }}>
                    <Box 
                        id="profile-header-container" 
                        sx={{
                            background: theme.palette.overlay!.subtle,
                            transition: 'all 0.3s ease-in-out'
                        }}
                    >
                        <Paper 
                            elevation={0} 
                            sx={{ 
                                p: 4, 
                                borderRadius: isLargeScreen ? 0 : 2, 
                                boxShadow: 'none', 
                                bgcolor: 'transparent',
                                borderBottom: isLargeScreen ? `1px solid ${theme.palette.divider}` : 'none'
                            }}
                        >
                            <Stack direction="column" spacing={2} alignItems="center" justifyContent="center">
                                <Box sx={{ position: 'relative' }}>
                                    <input
                                        ref={fileInputRef}
                                        hidden
                                        accept="image/jpeg,image/png,image/webp"
                                        type="file"
                                        onChange={handleImageUpload}
                                    />
                                    <Tooltip title="Profilbild ändern">
                                        <span>
                                            <IconButton
                                                onClick={handleAvatarClick}
                                                sx={{ p: 0 }}
                                                disabled={isUploading}
                                            >
                                                {isUploading ? (
                                                    <CircularProgress size={128} thickness={5} />
                                                ) : (
                                                    <Avatar
                                                        src={profile?.avatar ?? (profile?.profileImage ? `data:${profile.profileImage.contentType};base64,${profile.profileImage.data}` : undefined)}
                                                        sx={{
                                                            width: 128,
                                                            height: 128,
                                                            bgcolor: isLargeScreen 
                                                                ? theme.palette.overlay!.hover
                                                                : 'background.paper',
                                                            color: 'text.primary',
                                                            border: 1,
                                                            borderColor: 'divider',
                                                            boxShadow: isLargeScreen ? 0 : 1,
                                                            fontWeight: 700,
                                                            fontSize: 60,
                                                            backdropFilter: isLargeScreen ? 'blur(20px)' : 'none'
                                                        }}
                                                    >
                                                        {profile?.firstname?.[0]?.toUpperCase()}{profile?.lastname?.[0]?.toUpperCase()}
                                                    </Avatar>
                                                )}
                                                <PhotoCamera sx={{ 
                                                    position: 'absolute', 
                                                    bottom: 8, 
                                                    right: 8, 
                                                    fontSize: 32, 
                                                    bgcolor: isLargeScreen 
                                                        ? theme.palette.overlay!.glass
                                                        : 'background.paper', 
                                                    borderRadius: '50%', 
                                                    p: 0.5, 
                                                    color: 'text.secondary', 
                                                    border: 1, 
                                                    borderColor: 'divider', 
                                                    boxShadow: isLargeScreen ? 0 : 1,
                                                    backdropFilter: isLargeScreen ? 'blur(20px)' : 'none'
                                                }} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                                <Box>
                                    <Typography variant="h4" sx={{ 
                                        fontWeight: 700, 
                                        color: 'text.primary', 
                                        letterSpacing: 0.5, 
                                        mt: 2, 
                                        textAlign: 'center' 
                                    }}>
                                        {profile?.firstname} {profile?.lastname}
                                    </Typography>
                                    {profile?.email ? (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                mt: 0.75,
                                                textAlign: "center",
                                                color: "text.secondary",
                                                userSelect: "text"
                                            }}
                                        >
                                            {profile.email}
                                        </Typography>
                                    ) : null}
                                </Box>
                            </Stack>
                        </Paper>
                        <Container 
                            sx={{
                                bgcolor: 'transparent',
                                borderBottom: 1,
                                borderColor: 'divider'
                            }}
                            maxWidth={"lg"}
                        >
                            <Tabs
                                value={tab}
                                onChange={handleChange}
                                aria-label="profile tabs"
                                variant={isMobile ? "fullWidth" : "standard"}
                                sx={{
                                    '& .MuiTab-root': {
                                        minHeight: { xs: 56, sm: 64 },
                                        textTransform: 'none',
                                        fontSize: { xs: '0.875rem', sm: '1rem' },
                                        px: { xs: 2, sm: 3 },
                                        color: 'text.primary',
                                        '&.Mui-selected': {
                                            color: 'primary.main'
                                        }
                                    },
                                    '& .MuiTabs-indicator': {
                                        bgcolor: 'primary.main'
                                    }
                                }}
                            >
                                <Tab label="Dashboard" value={1} />
                                <Tab
                                    label={
                                        <Badge
                                            badgeContent={messageCount}
                                            color="primary"
                                            sx={{
                                                '& .MuiBadge-badge': {
                                                    right: -8,
                                                    top: 4
                                                }
                                            }}
                                        >
                                            Nachrichten
                                        </Badge>
                                    }
                                    value={3}
                                />
                                <Tab label="Einstellungen" value={4} />
                                <Tab label="Konto" value={5} />
                            </Tabs>
                        </Container>
                    </Box>
                    <Container 
                        maxWidth="lg"
                        disableGutters={isMobile}
                        sx={{
                            py: 6,
                            px: {
                                xs: 0,
                                sm: isTablet ? 2 : 3,
                                md: 4
                            },
                            minHeight: 600
                        }}
                    >
                        <Box sx={{ p: { xs: 0, sm: isTablet ? 2 : 3 }, minHeight: 600 }}>
                            <DashboardTab value={tab} index={1} refreshKey={dashboardRefreshKey} />
                            <MessagesTab
                                value={tab}
                                index={3}
                                onUnreadCountChange={setMessageCount}
                            />
                            <SettingsTab
                                value={tab}
                                index={4}
                                onGlobalSettingsChanged={() =>
                                    setDashboardRefreshKey((prev) => prev + 1)
                                }
                            />
                            <AccountTab value={tab} index={5} />
                        </Box>
                    </Container>
                </Box>
            </Box>

            {/* Google Profile Image Dialog */}
            <Dialog
                open={googleDialogOpen}
                onClose={() => setGoogleDialogOpen(false)}
                aria-labelledby="google-profile-image-dialog-title"
                aria-describedby="google-profile-image-dialog-description"
            >
                <DialogTitle id="google-profile-image-dialog-title">
                    Profilbild wird von Google synchronisiert
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="google-profile-image-dialog-description">
                        Dein Profilbild wird automatisch von Google synchronisiert. Um ein eigenes Profilbild zu verwenden, musst du die Google-Verknüpfung aufheben.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGoogleDialogOpen(false)} color="primary" autoFocus>
                        Verstanden
                    </Button>
                </DialogActions>
            </Dialog>
        </InAppContainer>
    );
};
